import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import BookingFormAuditAnalytics, {
  type BookingFormAuditStats,
} from "../../components/booking/BookingFormAuditAnalytics";
import BookingFormAuditServerFilters from "../../components/booking/BookingFormAuditServerFilters";
import PageVisitAnalytics, {
  type PageVisitStats,
} from "../../components/booking/PageVisitAnalytics";
import ConversionInsightsPanel, {
  type ConversionInsights,
} from "../../components/booking/ConversionInsightsPanel";
import { useBookingFormAuditQuery } from "../../hooks/useBookingFormAuditQuery";
import {
  ALL_TRAFFIC_FILTERS,
  DEFAULT_SESSION_FILTERS,
  type SessionClientFilters,
} from "../../utils/bookingFormAuditSession";

export default function BookingFormAnalytics() {
  const navigate = useNavigate();
  const {
    bookingSource,
    setBookingSource,
    customerEmail,
    setCustomerEmail,
    sessionId,
    setSessionId,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    buildQuery,
    sourceOptions,
  } = useBookingFormAuditQuery();

  const [clientFilters, setClientFilters] =
    useState<SessionClientFilters>(DEFAULT_SESSION_FILTERS);
  const [stats, setStats] = useState<BookingFormAuditStats | null>(null);
  const [pageStats, setPageStats] = useState<PageVisitStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [pageStatsLoading, setPageStatsLoading] = useState(true);
  const [insights, setInsights] = useState<ConversionInsights | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(true);

  const fetchStats = useCallback(
    async (signal?: AbortSignal) => {
      setStatsLoading(true);
      try {
        const query = buildQuery({
          includeSessionLimit: false,
          clientFilters,
        });
        const response = await axiosInstance.get(
          `/booking-form-audit/stats${query ? `?${query}` : ""}`,
          { signal },
        );
        setStats({
          ...response.data,
          trafficSources: response.data.trafficSources ?? [],
          topUtmTerms: response.data.topUtmTerms ?? [],
          topLandingPages: response.data.topLandingPages ?? [],
          avgEngagementSeconds: response.data.avgEngagementSeconds ?? null,
          avgScrollDepth: response.data.avgScrollDepth ?? null,
          callClicks: response.data.callClicks ?? 0,
          callClickSessions: response.data.callClickSessions ?? 0,
        });
      } catch (err) {
        if (
          (err as { code?: string; name?: string })?.code === "ERR_CANCELED" ||
          (err as { name?: string })?.name === "CanceledError"
        ) {
          return;
        }
        console.error(err);
        setStats(null);
      } finally {
        if (!signal?.aborted) setStatsLoading(false);
      }
    },
    [buildQuery, clientFilters],
  );

  const fetchPageStats = useCallback(
    async (signal?: AbortSignal) => {
      setPageStatsLoading(true);
      try {
        const query = buildQuery({
          includeSessionLimit: false,
          clientFilters,
        });
        const response = await axiosInstance.get(
          `/booking-form-audit/page-stats${query ? `?${query}` : ""}`,
          { signal },
        );
        setPageStats({
          ...response.data,
          topPages: response.data.topPages ?? [],
          pageViewsByDay: response.data.pageViewsByDay ?? [],
          pageEngagement: response.data.pageEngagement ?? [],
        });
      } catch (err) {
        if (
          (err as { code?: string; name?: string })?.code === "ERR_CANCELED" ||
          (err as { name?: string })?.name === "CanceledError"
        ) {
          return;
        }
        console.error(err);
        setPageStats(null);
      } finally {
        if (!signal?.aborted) setPageStatsLoading(false);
      }
    },
    [buildQuery, clientFilters],
  );

  const fetchInsights = useCallback(
    async (signal?: AbortSignal) => {
      setInsightsLoading(true);
      try {
        const params = new URLSearchParams();
        if (bookingSource) params.set("bookingSource", bookingSource);
        if (fromDate) params.set("from", new Date(fromDate).toISOString());
        if (toDate) {
          const end = new Date(toDate);
          end.setHours(23, 59, 59, 999);
          params.set("to", end.toISOString());
        }
        if (clientFilters.country) params.set("country", clientFilters.country);
        const query = params.toString();
        const response = await axiosInstance.get(
          `/booking-form-audit/conversion-insights${query ? `?${query}` : ""}`,
          { signal },
        );
        setInsights(response.data);
      } catch (err) {
        if (
          (err as { code?: string; name?: string })?.code === "ERR_CANCELED" ||
          (err as { name?: string })?.name === "CanceledError"
        ) {
          return;
        }
        console.error(err);
        setInsights(null);
      } finally {
        if (!signal?.aborted) setInsightsLoading(false);
      }
    },
    [bookingSource, fromDate, toDate, clientFilters.country],
  );

  const refreshAll = useCallback(() => {
    void fetchStats();
    void fetchPageStats();
    void fetchInsights();
  }, [fetchStats, fetchPageStats, fetchInsights]);

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      void fetchStats(controller.signal);
      void fetchPageStats(controller.signal);
      void fetchInsights(controller.signal);
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [fetchStats, fetchPageStats, fetchInsights]);

  const handleApplyListFilters = useCallback(
    (filters: SessionClientFilters) => {
      navigate("/booking-form-audit", { state: { clientFilters: filters } });
    },
    [navigate],
  );

  const isDefaultHumans =
    clientFilters.audience === "leads" && !clientFilters.country;

  const loading = statsLoading || pageStatsLoading || insightsLoading;

  return (
    <div>
      <PageBreadcrumb pageTitle="Booking Analytics" />
      <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
        Traffic, funnel, and conversion charts. For per-session events and customer
        details, open{" "}
        <Link to="/booking-form-audit" className="font-medium text-brand-600 hover:underline">
          Website Audit
        </Link>
        .
      </p>

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
          Audience
        </span>
        <button
          type="button"
          onClick={() => setClientFilters(DEFAULT_SESSION_FILTERS)}
          className={`rounded-full px-3 py-1.5 text-sm font-medium border ${
            isDefaultHumans
              ? "border-brand-500 bg-brand-50 text-brand-800"
              : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
          }`}
        >
          Humans (default)
        </button>
        <button
          type="button"
          onClick={() => setClientFilters(ALL_TRAFFIC_FILTERS)}
          className={`rounded-full px-3 py-1.5 text-sm font-medium border ${
            !isDefaultHumans
              ? "border-brand-500 bg-brand-50 text-brand-800"
              : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
          }`}
        >
          All traffic
        </button>
        <span className="text-xs text-gray-500">
          {isDefaultHumans
            ? "Charts exclude likely bots; all countries included."
            : "Showing unfiltered totals — includes bot noise."}
        </span>
      </div>

      <BookingFormAuditServerFilters
        sourceOptions={sourceOptions}
        bookingSource={bookingSource}
        fromDate={fromDate}
        toDate={toDate}
        customerEmail={customerEmail}
        sessionId={sessionId}
        onBookingSourceChange={setBookingSource}
        onFromDateChange={setFromDate}
        onToDateChange={setToDate}
        onCustomerEmailChange={setCustomerEmail}
        onSessionIdChange={setSessionId}
        onRefresh={refreshAll}
        loading={loading}
      />

      <div className="border-t border-gray-200 pt-6 dark:border-gray-800">
        <h2 className="mb-1 text-lg font-semibold text-gray-800 dark:text-white/90">
          Why visitors don't book
        </h2>
        <p className="mb-4 text-xs text-gray-500">
          Every non-bot visitor, including people who left without touching the form.
        </p>
      </div>
      <ConversionInsightsPanel insights={insights} loading={insightsLoading} />

      <PageVisitAnalytics stats={pageStats} loading={pageStatsLoading} />

      <div className="border-t border-gray-200 pt-6 dark:border-gray-800">
        <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-white/90">
          Booking form funnel
        </h2>
      </div>

      <BookingFormAuditAnalytics
        stats={stats}
        loading={statsLoading}
        onApplyFilters={handleApplyListFilters}
      />
    </div>
  );
}
